import { type FormEvent, useRef } from 'react';

import { useStartPasswordReset } from '@/hooks/use-start-password-reset.js';

import { Auth } from '@/service/amplifyAuth.ts';

import { isEmail } from '@/utils/formUtils.js';

type PrismicText = { text: string }[];

interface LoginFormContent {
    invalid_code_error: PrismicText;
    invalid_code_message: PrismicText;
    invalid_email_error_message: PrismicText;
    required_field_error_message: PrismicText;
}

interface LoginFormSubmitOptions {
    content: LoginFormContent;
    dataSet: boolean;
    email: string;
    password: string;
    code: string;
    verifyAccount: boolean;
    // Called with true right after the sign-up code is accepted; it rejects with NotAuthorizedException when the
    // password is wrong, so the hook can send the patient to reset it.
    signIn: (isJustConfirmed?: boolean) => Promise<void>;
    setError: (error: { title: string; text: string }) => void;
    setOpen: (open: boolean) => void;
    setLocalLoading: (loading: boolean) => void;
    setErrEmail: (message: string | false) => void;
    setErrPassword: (message: string) => void;
    onPasswordResetStarted?: () => void;
}

// Shared by the login page and the login dialog. A submit is ignored while the previous one is in flight,
// so a double click cannot send the same verification code twice.
export const useLoginFormSubmit = ({
    content,
    dataSet,
    email,
    password,
    code,
    verifyAccount,
    signIn,
    setError,
    setOpen,
    setLocalLoading,
    setErrEmail,
    setErrPassword,
    onPasswordResetStarted,
}: LoginFormSubmitOptions) => {
    const isSubmittingRef = useRef(false);
    const startPasswordReset = useStartPasswordReset();

    const showInvalidCodeError = () => {
        setError({
            title: content?.invalid_code_error[0].text,
            text: content?.invalid_code_message[0].text,
        });
        setOpen(true);
    };

    const verifyCode = async () => {
        console.debug('verifying');

        try {
            setLocalLoading(true);
            const result = await Auth.confirmSignUp(email, code);
            console.debug(result);
        } catch (error) {
            setLocalLoading(false);
            showInvalidCodeError();
            console.debug('error verifying:', error);
            return;
        }

        try {
            await signIn(true);
        } catch (error) {
            // The code verified the email, so a forgotten first password can be reset now.
            if ((error as { code?: string })?.code !== 'NotAuthorizedException') throw error;
            onPasswordResetStarted?.();
            await startPasswordReset(email);
        }
    };

    const submitForm = async () => {
        setErrEmail('');
        setErrPassword('');
        if (verifyAccount) {
            if (code.length !== 6) {
                showInvalidCodeError();
                return;
            }
            await verifyCode();
            return;
        }

        let err = false;
        if (email === '') {
            setErrEmail(content.required_field_error_message[0].text);
            err = true;
        } else if (!isEmail(email)) {
            setErrEmail(dataSet && content?.invalid_email_error_message[0].text);
            err = true;
        }

        if (password === '') {
            setErrPassword(content.required_field_error_message[0].text);
            err = true;
        }

        if (err) {
            return;
        }
        await signIn();
    };

    return async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;
        try {
            await submitForm();
        } finally {
            isSubmittingRef.current = false;
        }
    };
};
